import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLanguage } from "../../i18n";
import { API } from "../../constants";
import { trackContactFormConversion } from "../../utils/analytics";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_type: '',
    event_type: '',
    event_date: '',
    location: '',
    attendees: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, language } = useLanguage();

  const serviceTypes = [
    { value: 'drones', label: language === 'es' ? 'Show de Drones' : 'Drone Show' },
    { value: 'waterscreen', label: 'Water Screen' },
    { value: 'specialfx', label: language === 'es' ? 'Efectos Especiales' : 'Special Effects' },
    { value: 'elements', label: language === 'es' ? 'Elements by NexAir' : 'Elements by NexAir' },
  ];

  const eventTypes = [
    { value: 'massive', label: language === 'es' ? 'Evento Masivo' : 'Massive Event' },
    { value: 'corporate', label: language === 'es' ? 'Corporativo o Marca' : 'Corporate or Brand' },
    { value: 'realestate', label: language === 'es' ? 'Lanzamiento Inmobiliario' : 'Real Estate Launch' },
    { value: 'tourism', label: language === 'es' ? 'Turismo y Ciudades' : 'Tourism & Cities' },
    { value: 'private', label: language === 'es' ? 'Evento Privado' : 'Private Event' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/quotes`, formData);
      // Track Google Ads conversion
      await trackContactFormConversion(formData);
      toast.success(t('contact.success'));
      setFormData({ name: '', email: '', phone: '', company: '', service_type: '', event_type: '', event_date: '', location: '', attendees: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 md:py-32 bg-[#050505]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Info */}
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              {t('contact.title')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                {t('contact.titleHighlight')}
              </span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              {t('contact.info.title')}
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-gray-400">
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-cyan-400" />
                </div>
                <a href="mailto:ventas@nexair.com.mx" className="hover:text-cyan-400 transition-colors">
                  ventas@nexair.com.mx
                </a>
              </div>
              <div className="flex items-center gap-4 text-gray-400">
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-cyan-400" />
                </div>
                <a href="tel:+525578535742" className="hover:text-cyan-400 transition-colors">
                  +52 55 7853 5742
                </a>
              </div>
              <div className="flex items-center gap-4 text-gray-400">
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                </div>
                <span>{t('contact.info.address')}</span>
              </div>
            </div>
          </div>
          
          {/* Right - Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
            {/* Row 1: Name & Company */}
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder={t('contact.form.name')}
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors"
                data-testid="form-name"
              />
              <input
                type="text"
                placeholder={t('contact.form.company')}
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors"
                data-testid="form-company"
              />
            </div>

            {/* Row 2: Email & Phone */}
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                type="email"
                placeholder={t('contact.form.email')}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors"
                data-testid="form-email"
              />
              <input
                type="tel"
                placeholder={t('contact.form.phone')}
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors"
                data-testid="form-phone"
              />
            </div>

            {/* Row 3: Service Type & Event Type */}
            <div className="grid sm:grid-cols-2 gap-4">
              <select
                value={formData.service_type}
                onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                required
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white focus:border-cyan-500/50 focus:outline-none transition-colors appearance-none"
                data-testid="form-service-type"
              >
                <option value="" className="bg-[#0a0a0a] text-gray-500">
                  {language === 'es' ? 'Tipo de servicio *' : 'Service type *'}
                </option>
                {serviceTypes.map((type) => (
                  <option key={type.value} value={type.value} className="bg-[#0a0a0a]">
                    {type.label}
                  </option>
                ))}
              </select>
              <select
                value={formData.event_type}
                onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                required
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white focus:border-cyan-500/50 focus:outline-none transition-colors appearance-none"
                data-testid="form-event-type"
              >
                <option value="" className="bg-[#0a0a0a] text-gray-500">
                  {language === 'es' ? 'Tipo de evento *' : 'Event type *'}
                </option>
                {eventTypes.map((type) => (
                  <option key={type.value} value={type.value} className="bg-[#0a0a0a]">
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Row 4: Date & Location */}
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                type="date"
                placeholder={language === 'es' ? 'Fecha del evento' : 'Event date'}
                value={formData.event_date}
                onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors"
                data-testid="form-event-date"
              />
              <input
                type="text"
                placeholder={language === 'es' ? 'Ubicación del evento' : 'Event location'}
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors"
                data-testid="form-location"
              />
            </div>

            {/* Row 5: Attendees */}
            <input
              type="text"
              placeholder={language === 'es' ? 'Número de asistentes (opcional)' : 'Number of attendees (optional)'}
              value={formData.attendees}
              onChange={(e) => setFormData({...formData, attendees: e.target.value})}
              className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors"
              data-testid="form-attendees"
            />

            {/* Row 5: Message */}
            <textarea
              placeholder={t('contact.form.message')}
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={3}
              className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:outline-none transition-colors resize-none"
              data-testid="form-message"
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-4 rounded-xl transition-all disabled:opacity-50"
              data-testid="form-submit"
            >
              {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
